import {
  HubConnectionBuilder,
  HubConnection,
  LogLevel,
  HubConnectionState,
  RetryContext,
} from "@microsoft/signalr";
import { getAuthToken } from "../features/accounts/auth.service";
import { devLog } from "./util";
import { JourneyProgress } from "../features/guide/journey";

export const hubUrl = import.meta.env.VITE_API_URL + "/accountHub";

/**
 * A Concierge turn changed (S83 Ph B).
 *
 * 🔑 **This is a doorbell, not a payload.** It means "re-read the turn", and the receiver must
 * treat everything in it as a hint whose authority is the next poll. That discipline is the reason
 * push can be added here safely: SQL holds the state, so a hub that is down, a tab whose connection
 * dropped, or a multi-instance deploy with no backplane all cost latency and nothing else.
 */
export interface ConciergeTurnNudge {
  turnId: string;
  sessionId: string;
  status: string;
  progress?: string;
}

class AccountHubService {
  private connection: HubConnection;
  private connectionPromise: Promise<void> | null = null;
  private journeyCallbacks: ((data: {
    journeyType: string;
    stepKey: string;
  }) => void)[] = [];
  private conciergeTurnCallbacks: ((data: ConciergeTurnNudge) => void)[] = [];
  // Then every 60s for as long as the tab lives: a nightly restart or a deploy must not leave a
  // long-lived tab without its doorbell until reload (the old policy gave up after one failure).
  private readonly retryDelaysMs = [0, 2000, 10000, 30000];
  private readonly steadyRetryDelayMs = 60000;

  constructor() {
    this.connection = this.createConnection();
    this.setupEventHandlers();
  }

  private createConnection(): HubConnection {
    return new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => getAuthToken() || "",
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext: RetryContext) => {
          const delay =
            this.retryDelaysMs[retryContext.previousRetryCount] ??
            this.steadyRetryDelayMs;
          devLog(
            `Reconnecting in ${delay}ms (attempt ${
              retryContext.previousRetryCount + 1
            })`
          );
          return delay;
        },
      })
      // 🔑 Keep the server's ClientTimeoutInterval (Program.cs, 2 min) well above this. Pinging at
      // 30s against the server's default 30s timeout cut every hidden tab about once a minute,
      // because a hidden tab's timers are throttled and its ping always arrived late.
      .withServerTimeout(60000)
      .withKeepAliveInterval(15000)
      .configureLogging(
        process.env.NODE_ENV === "development" ? LogLevel.Debug : LogLevel.Error
      )
      .build();
  }

  private setupEventHandlers() {
    // Journey update handler
    this.connection.on("journeyUpdated", (data) => {
      this.journeyCallbacks.forEach((callback) => callback(data));
    });

    // Concierge turn doorbell — see ConciergeTurnNudge.
    this.connection.on("conciergeTurn", (data: ConciergeTurnNudge) => {
      this.conciergeTurnCallbacks.forEach((callback) => callback(data));
    });

    // Connection lifecycle handlers
    this.connection.onreconnecting((error) => {
      devLog("Reconnecting to hub...", error);
    });

    this.connection.onreconnected((connectionId) => {
      devLog("Reconnected to hub", connectionId);
    });

    // Retries never run out, so this means stop() was called. Clear the settled promise, or the
    // next connect() returns it and the tab stays disconnected.
    this.connection.onclose((error) => {
      devLog("Connection closed", error);
      this.connectionPromise = null;
    });
  }

  private async ensureConnected(): Promise<void> {
    if (this.connection.state === HubConnectionState.Disconnected) {
      await this.connect();
    }
  }

  async connect() {
    if (!this.connectionPromise) {
      this.connectionPromise = (async () => {
        try {
          await this.connection.start();
          devLog("Connected to account hub");
        } catch (err) {
          devLog("Failed to connect:", err);
          this.connectionPromise = null;
        }
      })();
    }
    return this.connectionPromise;
  }

  onJourneyUpdated(callback: (data: JourneyProgress) => void) {
    this.journeyCallbacks.push(callback);
    return () => {
      this.journeyCallbacks = this.journeyCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  /**
   * Subscribe to turn doorbells. Returns an unsubscribe.
   *
   * ⚠ The subscriber must still poll. This only ever makes the next read happen sooner, and a
   * subscriber that stops polling when the hub connects has quietly made the hub load-bearing —
   * which is precisely what this design avoids.
   */
  onConciergeTurn(callback: (data: ConciergeTurnNudge) => void) {
    this.conciergeTurnCallbacks.push(callback);
    return () => {
      this.conciergeTurnCallbacks = this.conciergeTurnCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  async disconnect() {
    try {
      if (
        this.connection &&
        this.connection.state !== HubConnectionState.Disconnected
      ) {
        await this.connection.stop();
        this.connectionPromise = null;
      }
    } catch (err) {
      devLog("Error disconnecting:", err);
    }
  }

  async sendEchoTest(message: string) {
    try {
      await this.ensureConnected();

      if (this.connection.state === HubConnectionState.Connected) {
        await this.connection.invoke("EchoTest", message);
      } else {
        devLog("Not connected. Current state:", this.connection.state);
      }
    } catch (err) {
      devLog("Error sending echo:", err);
      throw err;
    }
  }

  onEchoResponse(callback: (message: string) => void) {
    this.connection.on("EchoResponse", (message) => {
      devLog("Echo response received:", message);
      callback(message);
    });
  }
}

export const accountHub = new AccountHubService();
